'use client';

import 'swiper/css';

import Image from 'next/image';
import { useEffect, useMemo, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';

import { LineArrowLeftIcon, LineSearchIcon } from '@/app/_components/icons/line';
import Skeleton from '@/app/(football)/[team]/_components/common/Skeleton';
import type { _IStickerCard } from '@/app/(football)/[team]/_components/common/StickerCard/_hooks/useStickerCard';
import type { PackName } from '@/store/server/common/digital-card/_constant';
import {
  useGetAllCardCollectionWithInitQuery,
  useGetMyCardCollectionWithInitQuery,
} from '@/store/server/common/digital-card/digital-card.query';
import { useGetRealTimeEditionWithInitQuery } from '@/store/server/common/football/football.query';
import type { OriginalsName } from '@/store/server/common/originals/originals.dto';

import EditionFilter from './EditionFilter';
import MyCardItem from './MyCardItem';
import PackFilter from './PackFilter';
import SearchFilter from './SearchFilter';

interface MyCardListProps {
  originalsName: OriginalsName;
  packName: string;
  initialPackName?: string;
  initialOriginalsName?: OriginalsName;
  selectedStickerCard: _IStickerCard | null;
  onSelectCard: (metadataIdx: number, cardName: string, image: string) => void;
  teamSlug: string;
  history: Record<number, { packName: PackName; originalsName: OriginalsName }> | undefined;
}

type FilterMode = 'edition' | 'search';

const extractYear = (name: string) => name.match(/\d{4}/)?.[0] ?? '';

export default function MyCardList({ originalsName, packName, initialPackName, initialOriginalsName, selectedStickerCard, onSelectCard, teamSlug, history }: MyCardListProps) {

  const [selectedEdition, setSelectedEdition] = useState<string>('전체');
  const [searchText, setSearchText] = useState('');
  const [filterMode, setFilterMode] = useState<FilterMode>('edition');
  const [activePack, setActivePack] = useState({
    packName: initialPackName ?? packName,
    originalsName: initialOriginalsName ?? originalsName,
  });

  const packOptions = useMemo(() => {
    const items = [
      { label: extractYear(packName), packName, originalsName },
      ...Object.entries(history ?? {}).map(([year, config]) => ({
        label: year,
        packName: config.packName,
        originalsName: config.originalsName,
      })),
    ];
    return items.sort((a, b) => Number(b.label) - Number(a.label));
  }, [packName, originalsName, history]);

  const handlePackSelect = (newPackName: string, newOriginalsName: OriginalsName) => {
    setActivePack({ packName: newPackName, originalsName: newOriginalsName });
    setSelectedEdition('전체');
    setSearchText('');
    setFilterMode('edition');
  };

  const { data: allCardData, isLoading: isAllLoading } = useGetAllCardCollectionWithInitQuery({
    originalsName: activePack.originalsName as OriginalsName,
    packName: activePack.packName as PackName,
  });
  const { data: myCardData, isLoading: isMyLoading } = useGetMyCardCollectionWithInitQuery({
    originalsName: activePack.originalsName as OriginalsName,
    packName: activePack.packName as PackName,
  });
  const { data: editionData, isLoading: isEditionLoading } = useGetRealTimeEditionWithInitQuery({
    originalsName: activePack.originalsName as OriginalsName,
    packName: activePack.packName as PackName,
  });

  const isLoading = isAllLoading || isMyLoading;

  const editions = useMemo(() => {
    if (!editionData?.editionList) return ['전체'];
    return ['전체', ...editionData.editionList.map((e) => e.editionTitle)];
  }, [editionData]);

  const ownedMetadataIdxSet = useMemo(() => {
    if (!myCardData?.cardList) return new Set<number>();
    return new Set(myCardData.cardList.filter((c) => c.amount > 0).map((c) => c.metadataIdx));
  }, [myCardData]);

  const filteredCards = useMemo(() => {
    if (!allCardData?.cardList) return [];

    return allCardData.cardList.filter((card) => {
      if (!ownedMetadataIdxSet.has(card.metadataIdx)) return false;
      if (selectedEdition !== '전체' && card.editionTitle !== selectedEdition) return false;
      if (searchText && !card.cardName.toLowerCase().includes(searchText.toLowerCase())) return false;
      return true;
    });
  }, [allCardData, ownedMetadataIdxSet, selectedEdition, searchText]);

  // 팩 전환 후 첫 카드 자동 선택
  useEffect(() => {
    if (!isLoading && filteredCards.length > 0 && !selectedStickerCard) {
      const firstCard = filteredCards[0];
      onSelectCard(firstCard.metadataIdx, firstCard.cardName, firstCard.image);
    }
  }, [isLoading, filteredCards, selectedStickerCard, onSelectCard]);

  if (isLoading) {
    return (
      <div className="flex flex-col">
        {/* PackFilter skeleton */}
        <div className="flex gap-3 px-5 pt-[14px]">
          {[40, 40].map((w, i) => (
            <Skeleton key={i} width={w} height={32} />
          ))}
        </div>
        {/* EditionFilter skeleton */}
        <div className="flex items-center gap-4 px-5 pt-[6px] pb-[14px]">
          <div className="flex gap-3">
            {[56, 56, 56].map((w, i) => (
              <Skeleton key={i} width={w} height={32} />
            ))}
          </div>
        </div>
        {/* Card list skeleton */}
        <div className="flex gap-2 pl-5">
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex w-[65px] flex-col items-center">
              <Skeleton width={65} height={100} />
              <Skeleton width={50} height={16} className="mt-2" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      {/* Pack filter */}
      <div className="px-5 pt-[14px]">
        <PackFilter packs={packOptions} selectedPackName={activePack.packName} onSelect={handlePackSelect} />
      </div>

      {/* Filter toggle row */}
      <div className="flex items-center gap-4 px-5">
        {filterMode === 'edition' ? (
          <div className="flex w-full items-center gap-4 pt-[6px] pb-[14px]">
            <button type="button" onClick={() => setFilterMode('search')} className="shrink-0 cursor-pointer p-1">
              <LineSearchIcon size={16} color="#ffffff" />
            </button>
            <EditionFilter
              editions={editions}
              selected={selectedEdition}
              onSelect={setSelectedEdition}
              isLoading={isEditionLoading}
            />
          </div>
        ) : (
          <div className="flex w-full items-center gap-4 pb-2">
            <button
              type="button"
              onClick={() => {
                setFilterMode('edition');
                setSearchText('');
              }}
              className="shrink-0 cursor-pointer p-1"
            >
              <LineArrowLeftIcon size={16} color="#ffffff" />
            </button>
            <SearchFilter value={searchText} onChange={setSearchText} />
          </div>
        )}
      </div>

      {/* Card horizontal scroll */}
      {filteredCards.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-4 pt-5">
          <Image
            src={`https://cdn.kottonseed.io/assets/football/2026/${teamSlug}/icons/icon_illust_card.webp`}
            alt="empty"
            width={48}
            height={48}
          />
          <p className="typo-body-6 text-white">
            검색 결과가 없어요.
            <br />
            다시 검색해주세요.
          </p>
        </div>
      ) : (
        <Swiper slidesPerView="auto" spaceBetween={8} className="w-full !pl-5">
          {filteredCards.map((card) => (
            <SwiperSlide key={card.metadataIdx} style={{ width: 65 }}>
              <MyCardItem
                metadataIdx={card.metadataIdx}
                cardName={card.cardName}
                image={card.image}
                isSelected={selectedStickerCard?.metadataIdx === card.metadataIdx}
                onSelect={onSelectCard}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      )}
    </div>
  );
}
